import { useEffect, useState } from "react";
import { SPINESIM_MODALITIES } from "../../../classes/enums/MedicalImagesEnum";
import ToggleValidationButton from "../../../components/toggleValidationButton/ToggleValidationButton";
import { useRouteState } from "../../../hooks/useRouteState";
import { unvalidateInstanceInDatabase } from "../../../services/InstancesService";
import "./ValidationFlag.css";

const ValidationFlag = ({
  instances,
  refresh,
  modalityContext = null,
  disabled = false,
}) => {
  const [isValidatedInstances, setIsValidatedInstances] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const routeState = useRouteState();
  const { navigateToValidation } = routeState;

  useEffect(() => {
    if (instances && Object.keys(instances).length > 0) {
      // Filter instances based on modalityContext
      let relevantInstances = Object.values(instances);

      if (modalityContext === SPINESIM_MODALITIES.DX) {
        // Only check DX instances (sagittal and coronal)
        relevantInstances = Object.entries(instances)
          .filter(
            ([key]) =>
              key.includes("dx_instance") ||
              key === "sagittal_dx_instance_id" ||
              key === "coronal_dx_instance_id"
          )
          .map(([, value]) => value)
          .filter(instance => instance && instance._id);
      } else if (
        modalityContext === SPINESIM_MODALITIES.CT ||
        modalityContext === SPINESIM_MODALITIES.DRR
      ) {
        // Only check CT instance (for DRR)
        relevantInstances = Object.entries(instances)
          .filter(
            ([key]) => key.includes("ct_instance") || key === "ct_instance_id"
          )
          .map(([, value]) => value)
          .filter(instance => instance && instance._id);
      }

      const allValidated = relevantInstances.every(
        instance => instance.validated === true
      );
      setIsValidatedInstances(allValidated);
    }
  }, [instances, modalityContext]);

  const handleToggleValidation = async newState => {
    if (isUpdating || disabled) return;

    if (newState) {
      setIsValidatedInstances(newState);
      return;
    }

    setIsUpdating(true);

    try {
      if (instances && Object.keys(instances).length > 0) {
        // Filter instances to unvalidate based on modalityContext
        let instancesToUnvalidate = Object.values(instances);

        if (modalityContext === SPINESIM_MODALITIES.DX) {
          instancesToUnvalidate = Object.entries(instances)
            .filter(
              ([key]) =>
                key.includes("dx_instance") ||
                key === "sagittal_dx_instance_id" ||
                key === "coronal_dx_instance_id"
            )
            .map(([, value]) => value);
        } else if (
          modalityContext === SPINESIM_MODALITIES.CT ||
          modalityContext === SPINESIM_MODALITIES.DRR
        ) {
          instancesToUnvalidate = Object.entries(instances)
            .filter(
              ([key]) => key.includes("ct_instance") || key === "ct_instance_id"
            )
            .map(([, value]) => value);
        }

        instancesToUnvalidate = instancesToUnvalidate.filter(
          instance => instance && instance._id
        );

        await Promise.all(
          instancesToUnvalidate.map(instance =>
            unvalidateInstanceInDatabase(instance._id)
          )
        );

        setIsValidatedInstances(false);

        if (refresh) {
          await refresh();
        }

        await new Promise(resolve => setTimeout(resolve, 500));

        const validationMode =
          modalityContext === SPINESIM_MODALITIES.CT
            ? SPINESIM_MODALITIES.DRR
            : modalityContext;

        navigateToValidation(
          routeState.params.patientId,
          routeState.params.twinId,
          validationMode // Pass the modality context (DX or DRR) to validation route
        );
      } else {
      }
    } catch (error) {
      setIsValidatedInstances(true);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="App-validation-flag">
      <div className="App-validation-flag-wrapper">
        <div className="App-validation-flag-text">
          Values are auto-computed from the validated annotations
        </div>
        <div className="App-validation-flag-toggle">
          <div className="App-validation-flag-toggle-text">
            {isUpdating ? "Updating..." : "Validate"}
          </div>
          <ToggleValidationButton
            isValidated={isValidatedInstances}
            onToggle={handleToggleValidation}
            disabled={isUpdating || disabled}
          />
        </div>
      </div>
    </div>
  );
};

export default ValidationFlag;
